/**
 * Stockfish-powered AI for Chess
 * Provides strong chess play using Stockfish 18 WebAssembly
 */

import { getStockfishEngine } from './stockfishEngine';
import { getBookMove } from './openingBook';
import { Chess } from 'chess.js';

// Piece names for beginner explanations
const PIECE_NAMES = {
  p: 'pawn',
  n: 'knight',
  b: 'bishop',
  r: 'rook',
  q: 'queen',
  k: 'king'
};

// Convert UCI move to chess.js move format
function uciToMove(uciMove, board) {
  if (!uciMove || uciMove.length < 4) return null;
  
  const from = uciMove.substring(0, 2);
  const to = uciMove.substring(2, 4);
  const promotion = uciMove.length > 4 ? uciMove[4] : undefined;
  
  // Validate move is legal
  const tempBoard = new Chess(board.fen());
  const moves = tempBoard.moves({ verbose: true });
  const move = moves.find(m => m.from === from && m.to === to && 
    (!promotion || m.promotion === promotion));
  
  return move || null;
}

// Generate beginner-friendly explanation for a move
export function explainMove(move, _board) {
  if (!move) return "Analyzing position...";
  
  const piece = PIECE_NAMES[move.piece] || 'piece';
  const from = move.from;
  const to = move.to;
  
  // Check for special moves
  if (move.san === 'O-O') {
    return "Castle kingside - this protects your king and activates your rook!";
  }
  if (move.san === 'O-O-O') {
    return "Castle queenside - this protects your king and activates your rook!";
  }
  
  let explanation = `Move your ${piece} from ${from} to ${to}`;
  
  // Add capture info
  if (move.captured) {
    const capturedPiece = PIECE_NAMES[move.captured] || 'piece';
    explanation = `Capture their ${capturedPiece} with your ${piece}! (${from} to ${to})`;
  }
  
  // Add check info
  if (move.san && move.san.includes('+')) {
    explanation += " - this puts the king in check!";
  }
  
  // Add checkmate info
  if (move.san && move.san.includes('#')) {
    explanation = `CHECKMATE! Move ${piece} from ${from} to ${to} to win the game!`;
  }
  
  // Add context for why the move is good
  const centerSquares = ['d4', 'd5', 'e4', 'e5'];
  if (centerSquares.includes(to)) {
    explanation += " - controls the center";
  }
  
  // Development moves
  if (move.piece === 'n' || move.piece === 'b') {
    const startingSquares = ['b1', 'g1', 'c1', 'f1', 'b8', 'g8', 'c8', 'f8'];
    if (startingSquares.includes(from)) {
      explanation += " - develops a piece";
    }
  }
  
  // Promotion
  if (move.promotion) {
    explanation = `Push your pawn to ${to} and promote to a queen!`;
  }
  
  return explanation;
}

// Format evaluation for display
export function formatEvaluation(score, scoreType) {
  if (scoreType === 'mate') {
    if (score > 0) return `Mate in ${score}`;
    if (score < 0) return `Mated in ${Math.abs(score)}`;
    return 'Checkmate';
  }
  
  // Convert centipawns to pawns
  const pawns = (score / 100).toFixed(1);
  if (score > 0) return `+${pawns}`;
  return pawns;
}

// Convert Stockfish centipawn score to evaluation bar percentage
export function scoreToEvalPercent(score, scoreType) {
  if (scoreType === 'mate') {
    return score > 0 ? 95 : 5;
  }
  
  // Sigmoid function to map score to 0-100
  // Score of +300cp = ~75%, -300cp = ~25%
  const sigmoid = 1 / (1 + Math.exp(-score / 300));
  return Math.round(sigmoid * 100);
}

/**
 * Get best move using Stockfish
 * @param {Chess} board - chess.js board instance
 * @param {string} difficulty - 'easy', 'medium', 'hard', 'expert'
 * @returns {Promise<object>} - Move object with explanation
 */
export async function getBestMoveStockfish(board, difficulty = 'medium') {
  const engine = getStockfishEngine();
  
  try {
    await engine.init();
    engine.setDifficulty(difficulty);
    
    const fen = board.fen();
    
    // Check opening book first (for varied play)
    const bookMove = getBookMove(fen, board);
    if (bookMove) {
      return {
        ...bookMove,
        explanation: explainMove(bookMove, board),
        isBookMove: true,
        evaluation: { score: 0, scoreType: 'cp' }
      };
    }
    
    const result = await engine.getBestMove(fen);
    
    if (result && result.bestMove) {
      const move = uciToMove(result.bestMove, board);
      if (move) {
        return {
          ...move,
          explanation: explainMove(move, board),
          isBookMove: false,
          evaluation: result.evaluation || { score: 0, scoreType: 'cp' }
        };
      }
    }
    
    // Fallback to random legal move
    const moves = board.moves({ verbose: true });
    if (moves.length > 0) {
      const randomMove = moves[Math.floor(Math.random() * moves.length)];
      return {
        ...randomMove,
        explanation: explainMove(randomMove, board),
        isBookMove: false,
        evaluation: { score: 0, scoreType: 'cp' }
      };
    }
    
    return null;
  } catch (error) {
    console.error('Stockfish error:', error);
    // Return a random move as fallback
    const moves = board.moves({ verbose: true });
    if (moves.length > 0) {
      return moves[Math.floor(Math.random() * moves.length)];
    }
    return null;
  }
}

/**
 * Get coach suggestion using Stockfish analysis
 * @param {Chess} board - chess.js board instance
 * @returns {Promise<object>} - Suggested move with explanation and predicted response
 */
export async function getCoachSuggestion(board) {
  const engine = getStockfishEngine();
  
  try {
    await engine.init();
    
    const fen = board.fen();
    
    // Check opening book first
    const bookMove = getBookMove(fen, board);
    if (bookMove) {
      // Validate book move is legal
      const moves = board.moves({ verbose: true });
      const validBookMove = moves.find(m => m.from === bookMove.from && m.to === bookMove.to);
      
      if (validBookMove) {
        // Get predicted response
        const tempBoard = new Chess(board.fen());
        tempBoard.move(validBookMove);
        
        let predictedResponse = null;
        if (!tempBoard.isGameOver()) {
          const responseResult = await engine.getBestMove(tempBoard.fen(), 10);
          if (responseResult && responseResult.bestMove) {
            predictedResponse = uciToMove(responseResult.bestMove, tempBoard);
          }
        }
        
        return {
          ...validBookMove,
          explanation: explainMove(validBookMove, board),
          isBookMove: true,
          evaluation: { score: 0, scoreType: 'cp', depth: 0 },
          predictedResponse,
          mateIn: null
        };
      }
    }
    
    // Use high depth for coach analysis
    const result = await engine.analyze(fen, null, 18);
    
    if (result && result.bestMove) {
      const move = uciToMove(result.bestMove, board);
      if (move) {
        // Get predicted response (ponder move)
        let predictedResponse = null;
        if (result.ponderMove) {
          const tempBoard = new Chess(board.fen());
          tempBoard.move(move);
          predictedResponse = uciToMove(result.ponderMove, tempBoard);
        } else if (result.pv && result.pv.length > 1) {
          // Use PV line for prediction
          const tempBoard = new Chess(board.fen());
          tempBoard.move(move);
          predictedResponse = uciToMove(result.pv[1], tempBoard);
        }
        
        // Check for mate
        let mateIn = null;
        if (result.scoreType === 'mate' && result.score > 0) {
          mateIn = result.score;
        }
        
        // Parse full principal variation line
        let pvLine = [];
        if (result.pv && result.pv.length > 0) {
          let tempBoard = new Chess(board.fen());
          for (const uciMove of result.pv.slice(0, 10)) { // Up to 10 moves
            const pvMove = uciToMove(uciMove, tempBoard);
            if (pvMove) {
              pvLine.push({
                ...pvMove,
                san: pvMove.san
              });
              tempBoard.move(pvMove);
            } else {
              break;
            }
          }
        }
        
        return {
          ...move,
          explanation: explainMove(move, board),
          isBookMove: false,
          evaluation: {
            score: result.score || 0,
            scoreType: result.scoreType || 'cp',
            depth: result.depth || 0
          },
          predictedResponse,
          mateIn,
          pvLine // Full principal variation
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Coach suggestion error:', error);
    return null;
  }
}

/**
 * Analyze a position and return evaluation
 * @param {string} fen - Position in FEN notation
 * @returns {Promise<object>} - Analysis result
 */
export async function analyzePosition(fen) {
  const engine = getStockfishEngine();
  
  try {
    await engine.init();
    const result = await engine.analyze(fen, null, 15);
    
    return {
      score: result.score || 0,
      scoreType: result.scoreType || 'cp',
      bestMove: result.bestMove,
      depth: result.depth || 0,
      evalPercent: scoreToEvalPercent(result.score || 0, result.scoreType || 'cp')
    };
  } catch (error) {
    console.error('Position analysis error:', error);
    return { score: 0, scoreType: 'cp', evalPercent: 50 };
  }
}

/**
 * Check if current position has a forced mate
 * @param {string} fen - Position in FEN notation
 * @returns {Promise<number|null>} - Mate in N moves, or null if no mate
 */
export async function checkForMate(fen) {
  const engine = getStockfishEngine();
  
  try {
    await engine.init();
    const result = await engine.analyze(fen, null, 12);
    
    if (result.scoreType === 'mate') {
      return result.score;
    }
    return null;
  } catch (error) {
    console.error('Mate check error:', error);
    return null;
  }
}

// Export for backward compatibility
export const getBestMove = getBestMoveStockfish;
export const getBestMoveForCoach = getCoachSuggestion;
